import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CheckCircle2, XCircle, Clock, ScanLine, UserCheck, RefreshCw } from "lucide-react";
import { format, parseISO, subDays } from "date-fns";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Combined activity timeline for a student — attendance rows and pickup logs
 * interleaved by timestamp desc, with cursor pagination and a date-range filter.
 *
 * Backend: GET /api/students/{student_id}/activity
 * Feb 2026 — spec §3 of pending-enhancements bundle.
 */
export const StudentActivityLog = ({ studentId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

  // Filter state: "" (all recent) | "7d" | "30d" | "custom"
  const [filter, setFilter] = useState("");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const dateRange = useMemo(() => {
    if (filter === "7d") return { date_from: format(subDays(new Date(), 7), "yyyy-MM-dd") };
    if (filter === "30d") return { date_from: format(subDays(new Date(), 30), "yyyy-MM-dd") };
    if (filter === "custom" && (customFrom || customTo)) {
      const r = {};
      if (customFrom) r.date_from = customFrom;
      if (customTo) r.date_to = customTo;
      return r;
    }
    return {};
  }, [filter, customFrom, customTo]);

  const fetchPage = useCallback(async (reset = false) => {
    if (!studentId) return;
    setLoading(true);
    try {
      const params = { limit: 20, ...dateRange };
      if (!reset && nextCursor) params.before = nextCursor;
      const q = new URLSearchParams(params).toString();
      const res = await api.get(`/students/${studentId}/activity?${q}`);
      const rows = res.data.entries || [];
      setEntries(prev => (reset ? rows : [...prev, ...rows]));
      setHasMore(!!res.data.has_more);
      setNextCursor(res.data.next_cursor || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [studentId, nextCursor, dateRange]);

  // Reset & refetch on filter change or student change
  useEffect(() => {
    setEntries([]);
    setNextCursor(null);
    setHasMore(false);
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, filter, customFrom, customTo]);

  const renderEntry = (e, idx) => {
    const ts = e.timestamp ? parseISO(e.timestamp) : null;
    const dateStr = ts ? format(ts, "dd MMM yyyy, HH:mm") : "—";
    if (e.type === "attendance") {
      const status = (e.status || "").toLowerCase();
      const Icon = status === "present" ? CheckCircle2 : status === "absent" ? XCircle : Clock;
      const color = status === "present" ? "text-emerald-600 bg-emerald-50" : status === "absent" ? "text-red-600 bg-red-50" : "text-amber-600 bg-amber-50";
      return (
        <div
          key={`att-${idx}`}
          data-testid={`activity-entry-attendance-${idx}`}
          className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 bg-white hover:bg-slate-50 transition-colors"
        >
          <div className={`shrink-0 p-2 rounded-lg ${color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-slate-900">
                {status === "present" ? "Attended" : status === "absent" ? "Absent" : "Late"}
                {" — "}
                {e.batch_name || "Unknown class"}
              </p>
              {e.is_trial && (
                <span className="text-[10px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">
                  Trial
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {dateStr}
              {e.session_start_time && ` · ${e.session_start_time}–${e.session_end_time}`}
              {e.studio_name && ` · ${e.studio_name}`}
            </p>
          </div>
        </div>
      );
    }
    // pickup
    const isQR = e.method === "qr";
    return (
      <div
        key={`pk-${idx}`}
        data-testid={`activity-entry-pickup-${idx}`}
        className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 bg-white hover:bg-slate-50 transition-colors"
      >
        <div className="shrink-0 p-2 rounded-lg text-violet-700 bg-violet-50">
          {isQR ? <ScanLine className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900">
            Picked up{isQR ? " (QR scan)" : " (manual)"}
            {e.verified_by_name && ` · verified by ${e.verified_by_name}`}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {dateStr}
            {e.batch_name && ` · ${e.batch_name}`}
            {e.studio_name && ` · ${e.studio_name}`}
          </p>
          {e.notes && (
            <p className="text-xs text-slate-600 mt-1 italic break-words">
              {e.notes}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="student-activity-log" className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[140px]">
          <Label className="text-xs">Date range</Label>
          <select
            data-testid="activity-filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full h-10 rounded-lg border px-3 bg-white text-sm"
          >
            <option value="">Recent (last 20)</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        {filter === "custom" && (
          <>
            <div>
              <Label className="text-xs">From</Label>
              <Input
                data-testid="activity-filter-from"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-10"
              />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input
                data-testid="activity-filter-to"
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-10"
              />
            </div>
          </>
        )}
      </div>

      {/* Timeline */}
      {loading && entries.length === 0 && (
        <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading activity…
        </div>
      )}
      {!loading && entries.length === 0 && (
        <div className="text-center py-10 text-slate-500 text-sm" data-testid="activity-empty-state">
          No activity in the selected range.
        </div>
      )}
      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map(renderEntry)}
        </div>
      )}

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button
            data-testid="activity-load-more-btn"
            variant="outline"
            onClick={() => fetchPage(false)}
            disabled={loading}
            className="rounded-full"
          >
            {loading ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default StudentActivityLog;
